'use client';

import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';

const Collapsible = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <CollapsiblePrimitive.Root
      ref={ref}
      // className={cn('group/collapsible', className)}
      className={className}
      {...props}
    >
      {props.children}
    </CollapsiblePrimitive.Root>
  );
});

const collapsibleTriggerVariants = cva(
  [
    'has-focus-ring', // to prevent parent using focus-within to show ring against this element
    'w-full flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline focus-visible:underline'
  ],
  {
    variants: {
      chevron: {
        down: '[&[data-state=open]>svg]:rotate-180',
        right: '[&[data-state=open]>svg]:-rotate-90'
      }
    },
    defaultVariants: { chevron: 'down' }
  }
);

const collapsibleChevronVariants = cva('', {
  variants: {
    chevron: {
      down: 'h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180',
      right: 'size-5 shrink-0 text-fg-muted transition-transform duration-200'
    }
  },
  defaultVariants: { chevron: 'down' }
});

export type CollapsibleTriggerProps = React.ComponentPropsWithoutRef<
  typeof CollapsiblePrimitive.Trigger
> & {
  /**
   * `down` (default): a small chevron pointing down, flipping up when open.
   * `right`: the section header chevron of the designs — larger and muted,
   * pointing right when closed and up when open.
   */
  chevron?: NonNullable<
    VariantProps<typeof collapsibleTriggerVariants>['chevron']
  >;
};

const CollapsibleTrigger = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Trigger>,
  CollapsibleTriggerProps
>(({ className, children, chevron = 'down', ...props }, ref) => {
  const Chevron = chevron === 'right' ? ChevronRight : ChevronDown;
  return (
    <CollapsiblePrimitive.Trigger
      ref={ref}
      className={cn(collapsibleTriggerVariants({ chevron }), className)}
      data-expand-target="inline"
      {...props}
    >
      {children}
      <Chevron className={collapsibleChevronVariants({ chevron })} />
    </CollapsiblePrimitive.Trigger>
  );
});

CollapsibleTrigger.displayName = 'CollapsibleTrigger';

const CollapsibleContent = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <CollapsiblePrimitive.Content
    ref={ref}
    role="region"
    className={cn(
      'overflow-hidden text-sm transition-all data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up',
      className
    )}
    {...props}
  >
    <div className="pb-2 pt-1 w-full">{children}</div>
  </CollapsiblePrimitive.Content>
));

CollapsibleContent.displayName = 'CollapsibleContent';

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
